import { PropsWithChildren, useCallback } from 'react';
import { UserWithToken, Nullable } from '@ygoargentina/sdk/api/api.types';
import { useUserStore } from '@/store/users';
import { markInitComplete } from '@/utils/auth-init';
import {
  AuthProvider as SharedAuthProvider,
  useAuth as useSharedAuth,
  type AuthProviderConfig,
} from '@ygoargentina/ui/contexts/auth-provider';
import packageJson from '../../package.json';

// Re-export the hook with the same interface
export const useAuth = useSharedAuth;

// Re-export context for backwards compatibility
export { AuthContext } from '@ygoargentina/ui/contexts/auth-provider';

const AuthProvider = ({ children }: PropsWithChildren) => {
  const { setUser: setStoreUser } = useUserStore();

  // Handle auth check complete - sync to store and mark init complete
  const handleAuthCheckComplete = useCallback(
    (user: Nullable<UserWithToken>) => {
      if (user) {
        setStoreUser(user);
      } else {
        setStoreUser(null);
      }
      markInitComplete();
    },
    [setStoreUser]
  );

  // Handle login - sync to store
  const handleLogin = useCallback(
    (user: UserWithToken) => {
      setStoreUser(user);
    },
    [setStoreUser]
  );

  // Handle logout - clear store (React will handle router switch)
  const handleLogout = useCallback(() => {
    setStoreUser(null);
    // Don't do hard redirect - let React re-render with authRouter
    // The AppContent component will switch to authRouter when user is null
  }, [setStoreUser]);

  const config: AuthProviderConfig = {
    service: 'FOLDER',
    version: packageJson.version,
    // El store de folder (usePermission, sidebar) lee de acá, no del contexto.
    onUserRefreshed: setStoreUser,
    onAuthCheckComplete: handleAuthCheckComplete,
    onLogin: handleLogin,
    onLogout: handleLogout,
  };

  return (
    <SharedAuthProvider config={config}>
      {children}
    </SharedAuthProvider>
  );
};

export default AuthProvider;
